import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  doc,
  deleteDoc,
  Timestamp,
  query,
  where,
} from "firebase/firestore";
import { useCuerpo } from "../context/CuerpoContext";

function normalizarRut(rut) {
  const limpio = rut.replace(/[^0-9kK]/g, "").toUpperCase();
  const cuerpo = limpio.slice(0, -1);
  const dv = limpio.slice(-1);
  return `${cuerpo}-${dv}`;
}

const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutos: evita mostrar estado operativo antiguo por mucho tiempo

function leerCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.data)) return null;
    if (Date.now() - Number(parsed.savedAt || 0) > CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed.data;
  } catch (_) {
    return null;
  }
}

function guardarCache(key, data) {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({ savedAt: Date.now(), data })
    );
  } catch (_) {
    // Si Safari bloquea storage o no hay espacio, seguimos sin cache.
  }
}

function fechaDesdeFirestore(valor) {
  if (!valor) return null;
  if (typeof valor?.toDate === "function") return valor.toDate();
  if (typeof valor?.seconds === "number") return new Date(valor.seconds * 1000);
  if (typeof valor?._seconds === "number") return new Date(valor._seconds * 1000);
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? null : d;
}

export default function VisualizacionBomberos({ companiaFiltro, onCitacionesChange }) {
  const { cuerpoId } = useCuerpo();
  const [bomberos, setBomberos] = useState([]);
  const [citaciones, setCitaciones] = useState([]);
  const [horaActual, setHoraActual] = useState(new Date());

  useEffect(() => {
    const intervalo = setInterval(() => setHoraActual(new Date()), 60000);
    return () => clearInterval(intervalo);
  }, []);

  const jerarquia = [
    "Comandante", "2° Comandante", "Capitán", "Teniente 1°",
    "Teniente 2°", "Teniente 3°", "Teniente 4°", "Teniente 5°",
    "Ayudante General", "Ayudante", "Bombero Honorario", "Bombero", "Bombera"
  ];

  const mapaColores = {
    rojo: "#e74c3c", verde: "#27ae60", azul: "#2980b9", amarillo: "#f1c40f",
    naranja: "#e67e22", morado: "#8e44ad", celeste: "#3498db",
    gris: "#7f8c8d", negro: "#2c3e50"
  };

  const ordenarPorJerarquia = (lista) => [...lista].sort((a, b) => {
    const indexA = jerarquia.findIndex(cargo =>
      a.cargo?.toLowerCase().startsWith(cargo.toLowerCase()));
    const indexB = jerarquia.findIndex(cargo =>
      b.cargo?.toLowerCase().startsWith(cargo.toLowerCase()));
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
  });

  // === 🔥 Carga bomberos en servicio
  // Muestra cache de sesión inmediatamente y sincroniza Firestore en segundo plano.
  useEffect(() => {
    if (!cuerpoId || !companiaFiltro) return;

    const cacheKey = `bes:servicio:${cuerpoId}:${companiaFiltro}`;
    const cached = leerCache(cacheKey);
    if (cached) setBomberos(cached);

    // Filtrar también por compañía reduce documentos transferidos al abrir la vista.
    const qServicio = query(
      collection(db, "bomberos_en_servicio"),
      where("cuerpo_id", "==", cuerpoId),
      where("compania", "==", companiaFiltro)
    );

    const unsub = onSnapshot(
      qServicio,
      (snap) => {
        const lista = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setBomberos(lista);
        guardarCache(cacheKey, lista);
      },
      (error) => {
        console.error("❌ Error leyendo bomberos en servicio:", error);
      }
    );

    return () => unsub();
  }, [cuerpoId, companiaFiltro]);

  // === 🔥 Carga citaciones filtradas
  // Usa cache breve para que los avisos no retrasen la apertura de la compañía.
  useEffect(() => {
    if (!cuerpoId) return;

    const cacheKey = `bes:citaciones:${cuerpoId}`;
    const mapNombreANum = {
      "Primera Compañía": "1",
      "Segunda Compañía": "2",
      "Tercera Compañía": "3",
      "Cuarta Compañía": "4",
      "Quinta Compañía": "5"
    };

    const filtrarCitaciones = (lista) => {
      const now = new Date();
      const numDesdeNombre = mapNombreANum[companiaFiltro] || null;
      const posiblesValores = ["Todas"];
      if (companiaFiltro) posiblesValores.push(companiaFiltro);
      if (numDesdeNombre) {
        posiblesValores.push(numDesdeNombre);
        posiblesValores.push(Number(numDesdeNombre));
      }

      const activas = lista.filter((data) => {
        const fecha = fechaDesdeFirestore(data.fecha);
        return fecha && fecha > now && posiblesValores.includes(data.compania);
      });

      setCitaciones(activas);
      onCitacionesChange?.(activas.length);
    };

    const cached = leerCache(cacheKey);
    if (cached) filtrarCitaciones(cached);

    const qCitaciones = query(
      collection(db, "citaciones"),
      where("cuerpo_id", "==", cuerpoId)
    );

    const unsub = onSnapshot(
      qCitaciones,
      (snap) => {
        const lista = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        guardarCache(cacheKey, lista);
        filtrarCitaciones(lista);
      },
      (error) => {
        console.error("❌ Error leyendo citaciones:", error);
      }
    );

    return () => unsub();
  }, [companiaFiltro, onCitacionesChange, cuerpoId]);

  // === 🔥 Elimina bomberos fuera de tiempo
  // ✅ Soporta turnos nocturnos (ej: inicio 20:30, término 10:30 => término se asume día siguiente)
  // ✅ Mantiene margen de 1 minuto para evitar eliminaciones antes de tiempo
  useEffect(() => {
    const interval = setInterval(async () => {
      const nowMs = Timestamp.now().toMillis();
      const GRACE_MS = 60 * 1000; // 1 minuto margen
      const DAY_MS = 24 * 60 * 60 * 1000;

      for (const bombero of bomberos) {
        const ini = bombero.horaInicioServicio;
        const fin = bombero.horaTerminoServicio;

        const inicioMs =
          typeof ini?.toMillis === "function"
            ? ini.toMillis()
            : typeof ini?.seconds === "number"
              ? ini.seconds * 1000
              : null;

        let terminoMs =
          typeof fin?.toMillis === "function"
            ? fin.toMillis()
            : typeof fin?.seconds === "number"
              ? fin.seconds * 1000
              : null;

        // Si no hay término, no borramos
        if (!terminoMs) continue;

        // ✅ Turno nocturno: si hay inicio y el término quedó <= inicio,
        // asumimos que el término corresponde al día siguiente.
        if (inicioMs && terminoMs <= inicioMs) {
          terminoMs = terminoMs + DAY_MS;
        }

        // Si el inicio está en el futuro pero el término aparece en el pasado,
        // normalmente es un dato incoherente (zona horaria / guardado). No borramos.
        if (inicioMs && inicioMs > nowMs && terminoMs < nowMs) {
          console.warn("⚠️ Inicio futuro pero término pasado (NO se elimina):", {
            id: bombero.id,
            nombre: bombero.nombre,
            now: new Date(nowMs).toString(),
            inicio: new Date(inicioMs).toString(),
            termino: new Date(terminoMs).toString(),
          });
          continue;
        }

        // Borrado normal: solo si ya venció (con margen)
        if (nowMs >= terminoMs + GRACE_MS) {
          try {
            console.log(`🔥 Eliminando bombero vencido: ${bombero.nombre}`);
            await deleteDoc(doc(db, "bomberos_en_servicio", bombero.id));
          } catch (error) {
            console.error(`❌ Error eliminando ${bombero.nombre}:`, error);
          }
        }
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [bomberos]);

  // === 🔥 Elimina citaciones vencidas
  useEffect(() => {
    const interval = setInterval(async () => {
      const now = new Date();

      for (const cit of citaciones) {
        const fechaCitacion = fechaDesdeFirestore(cit.fecha);
        if (fechaCitacion && fechaCitacion <= now) {
          try {
            console.log(`🔥 Eliminando citación vencida: ${cit.id}`);
            await deleteDoc(doc(db, "citaciones", cit.id));
          } catch (error) {
            console.error(`❌ Error eliminando citación ${cit.id}:`, error);
          }
        }
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [citaciones]);

  const manejarSalirServicio = async (bombero) => {
    const rutIngresado = prompt(
      `Para sacar de servicio a ${bombero.nombre}, ingresa su RUT (ej:12345678-8):`
    );
    if (!rutIngresado) return;

    const rutNormalizado = normalizarRut(rutIngresado.trim());
    if (rutNormalizado === bombero.id) {
      try {
        await deleteDoc(doc(db, "bomberos_en_servicio", bombero.id));
        alert(`${bombero.nombre} ha salido de servicio.`);
      } catch (error) {
        console.error("❌ Error eliminando bombero:", error);
        alert("Error al intentar eliminar al bombero. Revisa la consola.");
      }
    } else {
      alert("❌ RUT incorrecto. No se realizó ningún cambio.");
    }
  };

  const bomberosOrdenados = ordenarPorJerarquia(bomberos);

  const colorTexto = bomberosOrdenados[0]?.color || "";
  const colorFondo = mapaColores[colorTexto.toLowerCase()] || "#999";

  return (
    <div style={{ padding: "1rem", marginTop: citaciones.length > 0 ? `${citaciones.length * 3}rem` : 0 }}>
      {citaciones.length > 0 && (
        <div style={{
          position: "fixed",
          top: 0, left: 0, right: 0,
          zIndex: 1000, fontWeight: "bold"
        }}>
          {citaciones.map((cit, idx) => {
            const fechaObj = fechaDesdeFirestore(cit.fecha);
            const fecha = fechaObj
              ? fechaObj.toLocaleString([], {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                  hour: '2-digit', minute: '2-digit', hour12: false
                })
              : "";
            const colores = ["#f39c12", "#27ae60", "#2980b9", "#e67e22", "#8e44ad"];
            const colorFondo = colores[idx % colores.length];
            return (
              <div key={idx} style={{
                backgroundColor: colorFondo,
                color: "#fff", padding: "0.5rem", textAlign: "center"
              }}>
                {cit.texto} | Fecha y hora: {fecha}
              </div>
            );
          })}
        </div>
      )}

      {/* Reloj + encabezado */}
      <div style={{
        backgroundColor: colorFondo,
        color: "#fff",
        padding: "1rem",
        borderRadius: "10px 10px 0 0",
        fontSize: "1.5rem",
        fontWeight: "bold",
        textAlign: "center"
      }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: "1rem" }}>
          <span>{companiaFiltro}</span>
          <span style={{ fontSize: "1rem", display: "flex", alignItems: "center", gap: "0.3rem" }}>
            ⏰ {horaActual.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
          </span>
        </div>
      </div>

      {/* Listado de bomberos */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
        {bomberosOrdenados.map((bombero) => {
          const esJerarquico = ["comandante", "2° comandante", "capitán", "teniente"]
            .some(c => bombero.cargo?.toLowerCase().startsWith(c));
          const borderColor = esJerarquico ? colorFondo : "#ccc";
          const horaInicio = bombero.horaInicioServicio
            ? new Date(bombero.horaInicioServicio.seconds * 1000)
              .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
            : "";
          const horaTermino = bombero.horaTerminoServicio
            ? new Date(bombero.horaTerminoServicio.seconds * 1000)
              .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
            : "";

          return (
            <div key={bombero.id} style={{
              width: 150, margin: "0.5rem", backgroundColor: "#fff",
              border: `2px solid ${borderColor}`, borderRadius: 10,
              padding: "0.5rem", textAlign: "center"
            }}>
              {bombero.fotoURL ? (
                <img src={bombero.fotoURL} alt={bombero.nombre} loading="lazy" decoding="async" style={{
                  width: "100%", aspectRatio: "3/4", objectFit: "cover", borderRadius: 5
                }} />
              ) : (
                <div style={{
                  width: "100%", aspectRatio: "3/4", backgroundColor: "#ddd",
                  display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 5
                }}>
                  SIN FOTO
                </div>
              )}
              <div style={{ fontWeight: "bold" }}>{bombero.nombre}</div>
              <div>{bombero.cargo}</div>
              <div>{bombero.ubicacion}</div>
              {(horaInicio && horaTermino) && (
                <div style={{ fontSize: "0.85rem", marginTop: "0.3rem" }}>
                  {horaInicio} - {horaTermino}
                </div>
              )}
              {bombero.maquinista && (
                <>
                  <strong>Conductor: 🚒</strong><br />
                  <strong>{bombero.unidades?.join(", ") || "Ninguna"}</strong><br />
                </>
              )}
              <button
                style={{ marginTop: "0.5rem", fontSize: "0.8rem" }}
                onClick={() => manejarSalirServicio(bombero)}
              >
                Salir
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
